import React, { useState, useRef, useEffect } from "react"
import { SelectAsync, Select, Button, Input, LoadingFull, TextArea, MarkdownTextArea } from '@arnsbogroup/react-voicearchive'
import SupplierSelect from 'components/SupplierSelect'
import messages from "./messages"
import { useIntl, FormattedMessage } from 'react-intl'
import useApi from 'hooks/useApi'
import debounce from 'debounce-promise'
import BuyoutsTable from './components/BuyoutsTable'
import AddLiveSession from './components/AddLiveSession'
import useCurrentUser from 'hooks/useCurrentUser'
import useValidate, { is } from '@arnsbogroup/react-voicearchive/hooks/useValidate'
import DateTime from 'react-datetime'

const AddVersion = ({onVersionClose, orderId, defaultDeadline, refetchOrder, duplicateEntry = null}) => {
  const { formatMessage } = useIntl()
  const { roles = [] } = useCurrentUser()
  const containerRef = useRef(null)

  const emptyVersion = {
    suppliers: (duplicateEntry?.supplier && duplicateEntry?.taskId) ? [{
      value: duplicateEntry?.supplier.id,
      label: duplicateEntry?.supplier.userId
    }] : [], //Don't duplicate suppliers. Only duplicate when editing existing task
    language: duplicateEntry?.language ? {
      value: duplicateEntry?.language.id,
      label: duplicateEntry?.language.name
    } : {
      value: '',
      label: ''
    },
    price: duplicateEntry?.price ? duplicateEntry?.price : 0,
    priceCurrency: duplicateEntry?.priceCurrency.id ? {
      value: duplicateEntry?.priceCurrency.id,
      label: duplicateEntry?.priceCurrency.iso
    } : {
      value: 2,
      label: 'EUR'
    },
    cost:  duplicateEntry?.cost ? duplicateEntry?.cost : 0,
    costCurrency: duplicateEntry?.costCurrency.id ? {
      value: duplicateEntry?.costCurrency.id,
      label: duplicateEntry?.costCurrency.iso
    } : {
      value: 2,
      label: 'EUR'
    },
    createLiveSessionTask: !!duplicateEntry?.lsTask,
    liveSessionCost: !!duplicateEntry?.lsTask ? duplicateEntry?.lsTask.lsCost : 0,
    liveSessionCostCurrency: !!duplicateEntry?.lsTask ? {
      value: duplicateEntry?.lsTask.lsCostCurrency.id,
      label: duplicateEntry?.lsTask.lsCostCurrency.iso
    } : {
      value: 2,
      label: 'EUR'
    },
    liveSessionPrice: !!duplicateEntry?.lsTask ? duplicateEntry?.lsTask.lsPrice : 0,
    liveSessionPriceCurrency: !!duplicateEntry?.lsTask ? {
      value: duplicateEntry?.lsTask.lsPriceCurrency.id,
      label: duplicateEntry?.lsTask.lsPriceCurrency.iso
    } : {
      value: 2,
      label: 'EUR'
    },
    buyouts: !!duplicateEntry?.buyouts.length ? duplicateEntry?.buyouts.map((buyout) => {
      return {
        id: buyout.id,
        quantity: buyout.quantity,
        quantity_type: {
          value: buyout.quantity_type.id,
          label: buyout.quantity_type.name
        },
        media: {
          value: buyout.ordertargetmedia.id,
          label: buyout.ordertargetmedia.name
        },
        usage: {
          value: buyout.orderusage.id,
          label: buyout.orderusage.name
        },
        territoryMarket: {
          value: buyout.territoryMarket.id,
          label: buyout.territoryMarket.name
        },
        cost: buyout.cost,
        costCurrency: {
          value: buyout.costCurrency.id,
          label: buyout.costCurrency.iso
        },
        price: buyout.price,
        priceCurrency: {
          value: buyout.priceCurrency.id,
          label: buyout.priceCurrency.iso
        },
      }
    }).filter((buyout) => buyout.media.value && buyout.usage.value) : [],
    liveSessionDates: !!duplicateEntry?.lsTask && !!duplicateEntry?.lsTask.lsDates.length ?
      duplicateEntry?.lsTask.lsDates.map((lsDate) => {
        return {
          dateStart: lsDate.dateStart ? new Date(parseInt(lsDate.dateStart) * 1000) : null,
          dateEnd: lsDate.dateEnd ? new Date(parseInt(lsDate.dateEnd) * 1000) : null,
          message: lsDate.message
        }
      }).filter((lsDate) => lsDate.dateStart && lsDate.dateEnd) : [],
    voTotalPrice: 0,
    voTotalCost: 0,
    voTotalPriceCurrency: {
      value: 2,
      label: 'EUR'
    },
    voTotalCostCurrency: {
      value: 2,
      label: 'EUR'
    },
    brief: duplicateEntry?.brief ? duplicateEntry?.brief : '',
    taskId: duplicateEntry?.taskId ? duplicateEntry?.taskId : null,
    deadline: duplicateEntry?.deadline ? duplicateEntry?.deadline : defaultDeadline
  }

  const editing = !!emptyVersion.taskId
  const [version, setVersion] = useState(emptyVersion)
  const [loading, setLoading] = useState(false)
  const [languageOptions] = useApi(`/rest/v2/language`)

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' })
    }
  }, [])

  const addVersion = () => {
    if (!validateAudition()) {
      return
    }

    setLoading(true)

    let body = new FormData()
    body.append('suppliers', JSON.stringify(version.suppliers))
    body.append('cost', version.cost)
    body.append('costCurrency', version.costCurrency.value)
    body.append('price', version.price)
    body.append('priceCurrency', version.priceCurrency.value)
    body.append('liveSessionCost', version.liveSessionCost)
    body.append('liveSessionCostCurrency', version.liveSessionCostCurrency.value)
    body.append('liveSessionPrice', version.liveSessionPrice)
    body.append('liveSessionPriceCurrency', version.liveSessionPriceCurrency.value)
    body.append('liveSessionDates', JSON.stringify(version.liveSessionDates))
    body.append('language', version.language.value)

    body.append('createLiveSessionTask', version.createLiveSessionTask)
    body.append('buyouts', JSON.stringify(version.buyouts.filter((buyout) =>
      buyout.media.value && buyout.usage.value && buyout.territoryMarket.value
    )))
    body.append('brief', version.brief)
    body.append('deadline', version.deadline)

    fetch(`/rest/v2/order/${orderId}/audition-scope-overview`, {
      credentials: 'same-origin',
      method: editing ? 'PUT' : 'POST',
      body: editing ? JSON.stringify({
          taskId: version.taskId,
          buyouts: version.buyouts
        }) : body
    })
    .then((response) => response.json())
    .then(({success, errors}) => {
      if (success) {
        toastr.success(formatMessage(messages.versionAddSuccess))
        refetchOrder()
        onVersionClose()
      } else {
        toastr.error(errors.join(', '))
      }
      setLoading(false)
    })
    .catch((error) => {
      toastr.error(error)
      setLoading(false)
    })
  }

  const onBuyoutChange = (buyouts) => {
    let totalVoPrice = 0
    let totalVoPriceCurrency = version.voTotalPriceCurrency
    let totalVoCost = 0
    let totalVoCostCurrency = version.voTotalCostCurrency
    buyouts.forEach(buyout => {
      totalVoPrice += parseFloat(buyout.price) || 0
      totalVoCost += parseFloat(buyout.cost) || 0
      totalVoPriceCurrency = buyout.priceCurrency
      totalVoCostCurrency = buyout.costCurrency
    })

    setVersion({
      ...version,
      voTotalPrice: totalVoPrice,
      voTotalCost: totalVoCost,
      voTotalPriceCurrency: totalVoPriceCurrency,
      voTotalCostCurrency: totalVoCostCurrency,
      buyouts: buyouts
    })
  }

  const i18n = (errorMessages) => errorMessages?.map((errorMessage) => ({
    present: formatMessage(messages.errorsPresent)
  }[errorMessage] || errorMessage))

  const [auditionValidations, validateAudition] = useValidate({
    supplier: version?.suppliers[0] || null,
    language: version?.language?.label
  }, {
    supplier: [is.present],
    language: [is.present]
  })

  return (
    <div className="h-full" ref={containerRef}>
      { loading ? (
          <div className="grow h-full">
            <LoadingFull />
          </div>
        ) : (
          <div>
            <div className="flex flex-col-2 gap-2 p-2 w-full">
              <div className="w-full">
                <Select
                  required
                  size="sm"
                  label={formatMessage(messages.selectLanguageLabel)}
                  value={version.language}
                  onChange={(value) => setVersion({...version, language: value})}
                  options={languageOptions?.map(({ uid, name }) => ({ value: uid, label: name })) }
                  errors={i18n(auditionValidations.language)}
                  isDisabled={loading || editing}
                />
              </div>
              <div className="w-full">
                <SupplierSelect
                  isMulti
                  required
                  value={version.suppliers}
                  onChange={(value) => setVersion({
                    ...version,
                    suppliers: value ? value.map((supplier) => ({
                      value: supplier.value,
                      label: supplier.label
                    })) : []
                  })}
                  searchLanguageId={version?.language?.value}
                  className="bg-white"
                  size="sm"
                  errors={i18n(auditionValidations.supplier)}
                  isDisabled={loading || editing}
                />
              </div>
            </div>
            <div className="flex flex-col-5 gap-2 p-2">
            <div className="flex w-full gap-2">
                <Input
                  size="sm"
                  label={formatMessage(messages.inputCostLabel)}
                  type="number"
                  value={version.cost}
                  onChange={({ target: { value } }) => setVersion({...version, cost: value})}
                  disabled={loading || editing}
                />

                <SelectAsync
                  containerProps={{className: 'w-24'}}
                  className="mt-1"
                  size="sm"
                  label={formatMessage(messages.selectCostCurrencyLabel)}
                  value={[version.costCurrency]}
                  onChange={(value) => setVersion({...version, costCurrency: value})}
                  options={[]}
                  cacheOptions
                  defaultOptions
                  loadOptions={
                    debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                      .then((response) => response.json())
                      .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                      , 250)
                  }
                  isDisabled={loading || editing}
                />
              </div>
              <div className="flex w-full gap-2">
                <Input
                  size="sm"
                  label={formatMessage(messages.inputPriceLabel)}
                  type="number"
                  value={version.price}
                  onChange={({ target: { value } }) => setVersion({...version, price: value})}
                  disabled={loading || editing}
                />

                <SelectAsync
                  size="sm"
                  label={formatMessage(messages.selectPriceCurrencyLabel)}
                  containerProps={{className: 'w-24'}}
                  className="mt-1"
                  value={[version.priceCurrency]}
                  onChange={(value) => setVersion({...version, priceCurrency: value})}
                  options={[]}
                  cacheOptions
                  defaultOptions
                  loadOptions={
                    debounce((query) => fetch(`/rest/v1/currency?search[iso]=${query}`)
                      .then((response) => response.json())
                      .then((data) => data.map(({ uid, iso }) => ({ value: uid, label: iso })))
                      , 250)
                  }
                  isDisabled={loading || editing}
                />
              </div>
              <div className="w-full space-y-2">
                <div className="flex w-full gap-2">
                  <Input
                    size="sm"
                    label={formatMessage(messages.voTotalCost)}
                    value={version.voTotalCost}
                    disabled
                  />

                  <Input
                    size="sm"
                    containerProps={{className: 'w-24'}}
                    className="mt-1"
                    label={formatMessage(messages.voTotalCostCurrency)}
                    value={version.voTotalCostCurrency.label}
                    disabled
                  />
                </div>
                <p className="text-xs">
                  <FormattedMessage {...messages.voTotalCostDescription} />
                </p>
              </div>
              <div className="w-full space-y-2">
                <div className="flex w-full gap-2">
                  <Input
                    size="sm"
                    label={formatMessage(messages.voTotalPrice)}
                    value={version.voTotalPrice}
                    disabled
                  />

                  <Input
                    size="sm"
                    containerProps={{className: 'w-24'}}
                    className="mt-1"
                    label={formatMessage(messages.voTotalPriceCurrency)}
                    value={version.voTotalPriceCurrency.label}
                    disabled
                  />
                </div>
                <p className="text-xs">
                  <FormattedMessage {...messages.voTotalPriceDescription} />
                </p>
              </div>
            </div>
            <div className="w-full p-2">
              <div className="flex w-full gap-2">
                <div className="w-full">
                  <label className="text-gray-700 text-sm font-medium"><FormattedMessage {...messages.deadline} /></label>
                  <DateTime
                    className="w-full"
                    value={new Date(+version.deadline * 1000)}
                    inputProps={{
                      disabled: loading || editing,
                      className: "h-9 mt-1 px-2 rounded-sm border-t-gray-300 border w-full"
                    }}
                    onChange={(value) => setVersion({...version, deadline: Math.round(new Date(value).getTime() / 1000)})}
                    closeOnTab
                  />
                </div>
                <div className="w-full">
                  <MarkdownTextArea
                    label={formatMessage(messages.brief)}
                    value={version.brief}
                    onChange={(value) => setVersion({...version, brief: value})}
                    disabled={loading || editing}
                  />
                </div>
              </div>
            </div>
            <BuyoutsTable
              priceCurrency={version.priceCurrency}
              costCurrency={version.costCurrency}
              buyouts={version.buyouts}
              onBuyoutChange={buyouts => onBuyoutChange(buyouts)}
              editing={editing}
            />
            <AddLiveSession
              version={version}
              setVersion={(newVersion) => setVersion(newVersion)}
              editing={editing}
            />
            <div className="m-2 flex flex-col-2 gap-2 justify-end">
              <Button size="sm" variant="outline" state="negative" onClick={() => onVersionClose()}>
                <FormattedMessage {...messages.cancel} />
              </Button>
              <Button size="sm" onClick={() => addVersion()}>
                {loading ? <FormattedMessage {...messages.loading} /> : editing ? <FormattedMessage {...messages.save} /> : <FormattedMessage {...messages.create} />}
              </Button>
            </div>
          </div>
        )
      }
    </div>
  )
}

export default AddVersion
